// Message content validation utility
// Blocks profanity, external platform references, and contact information

const PROFANITY_LIST = [
  'fuck', 'shit', 'bitch', 'ass', 'damn', 'hell', 'cunt', 'dick', 
  'cock', 'pussy', 'bastard', 'asshole', 'motherfucker', 'bullshit',
  'piss', 'slut', 'whore', 'fag', 'nigger', 'retard', 'crap'
];

const EXTERNAL_PLATFORMS = [
  // Trading platforms
  'letstradepedals', 'ebay', 'craigslist', 'facebook marketplace',
  'offerup', 'mercari', 'gearpage', 'gearslutz',
  // Social media
  'reddit', 'discord', 'instagram', 'facebook', 'twitter', 'snapchat', 
  'whatsapp', 'telegram', 'kik',
  // Communication (removed 'signal' and 'outlook' - too common in pedal context)
  'gmail', 'yahoo', 'hotmail', 'protonmail',
  // Payment platforms
  'venmo', 'paypal', 'cashapp', 'zelle'
];

const CONTACT_PATTERNS = [
  // Email patterns
  /\b[a-z0-9._%+-]+(@|\(at\)|\[at\]|\s+at\s+)[a-z0-9.-]+(\.|dot|\(dot\)|\[dot\]|\s+dot\s+)(com|net|org|edu|io|co)\b/i,
  // Phone patterns
  /\b\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/,
  // Social media handles (only catch specific platform references, not generic @mentions)
  /\b(insta|instagram|discord|snapchat|twitter|telegram|tiktok)[\s:]+[a-z0-9_#]{3,}/i,
  // URLs (strict: must have protocol OR www, OR be a clear domain with 3+ chars before TLD)
  /\b(https?:\/\/|www\.)[^\s]+/i,
  /\b[a-z0-9-]{4,}\.(com|net|org|io|co)(?:\s|$|[^\w])/i
];

/**
 * Check if text contains profanity
 * Uses word boundaries to avoid false positives (e.g., "class" contains "ass")
 * Handles common obfuscation like: f*ck, f**k, fu©k, etc.
 */
export function containsProfanity(text) {
  if (!text || typeof text !== 'string') return false;
  
  const normalized = text.toLowerCase()
    .replace(/[*@#$%&]/g, '') // Remove common obfuscation characters
    .replace(/[0]/g, 'o')      // Replace 0 with o
    .replace(/[1]/g, 'i')      // Replace 1 with i
    .replace(/[3]/g, 'e')      // Replace 3 with e
    .replace(/[5]/g, 's')      // Replace 5 with s
    .replace(/[7]/g, 't');     // Replace 7 with t
  
  for (const word of PROFANITY_LIST) {
    // Use word boundaries to avoid false positives
    // \b matches word boundaries (start/end of word)
    const pattern = new RegExp(`\\b${word}\\b`, 'i');
    if (pattern.test(normalized)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if text contains references to external platforms
 */
export function containsExternalPlatform(text) {
  if (!text || typeof text !== 'string') return false;
  
  const normalized = text.toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[_\-./]/g, '');
  
  for (const platform of EXTERNAL_PLATFORMS) {
    const cleanPlatform = platform.replace(/\s+/g, '');
    if (normalized.includes(cleanPlatform)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Check if text contains contact information (email, phone, social handles, URLs)
 */
export function containsContactInfo(text) {
  if (!text || typeof text !== 'string') return false;
  
  for (const pattern of CONTACT_PATTERNS) {
    if (pattern.test(text)) {
      return true;
    }
  }
  
  return false;
}


/**
 * YouTube is the one link members may share in messages, comments and notes
 * (owner, 2026-11-01): a demo of the pedal is on-topic, every other link is
 * still blocked. The matcher is deliberately narrow: an optional scheme, an
 * optional www./m./music. subdomain, then EXACTLY youtube.com/<watch|shorts|
 * embed|live> or youtu.be with an 11-character video id, then anything up to
 * whitespace. Matches are removed BEFORE the link and contact checks run, so
 * "youtube.com.evil.example/..." (the host does not end at youtube.com) and
 * "https://evil.example/?u=youtube.com/watch?v=..." (the leftover
 * "https://evil.example/?u=" is still a URL) both stay blocked.
 */
// Review 2026-11-01: the first version swallowed everything up to the next
// space after the video id, so "youtu.be/<id>?x=me@gmail.com" lost the email
// before the contact check ran. Now only KNOWN YouTube query parameters
// (t, list, index, si, feature, pp, start, end, ab_channel, app, utm_*) with a
// plain value may accompany the id, and the link must end at whitespace,
// punctuation or the end of text. Anything else glued on means the link is
// not recognised at all, so the whole thing stays visible to the checks.
export const YOUTUBE_LINK_PATTERN = /\b(?:https?:\/\/)?(?:(?:www|m|music)\.)?(?:youtube\.com\/(?:watch\?(?:(?:t|list|index|si|feature|pp|start|end|ab_channel|app|utm_[a-z]+)=[\w%-]*&)*v=[\w-]{11}(?:&(?:t|list|index|si|feature|pp|start|end|ab_channel|app|utm_[a-z]+)=[\w%-]*)*|(?:shorts|embed|live)\/[\w-]{11}(?:\?(?:t|list|index|si|feature|pp|start|end|ab_channel|app|utm_[a-z]+)=[\w%-]*(?:&(?:t|list|index|si|feature|pp|start|end|ab_channel|app|utm_[a-z]+)=[\w%-]*)*)?)|youtu\.be\/[\w-]{11}(?:\?(?:t|list|index|si|feature|pp|start|end|ab_channel|app|utm_[a-z]+)=[\w%-]*(?:&(?:t|list|index|si|feature|pp|start|end|ab_channel|app|utm_[a-z]+)=[\w%-]*)*)?)(?:#[\w-]*)?(?=[\s.,;:!?)]|$)/gi;

/** The text with every allowed (YouTube) link removed, for policy checks. */
export function stripAllowedLinks(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(YOUTUBE_LINK_PATTERN, ' ');
}

/**
 * Validate message for all policy violations
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateMessage(text) {
  if (!text || typeof text !== 'string' || !text.trim()) {
    return { isValid: false, error: 'Message cannot be empty' };
  }

  // YouTube links are allowed; everything else is judged on what remains.
  const scrubbed = stripAllowedLinks(text);
  if (containsProfanity(scrubbed) || containsExternalPlatform(scrubbed) || containsContactInfo(scrubbed)) {
    return {
      isValid: false,
      error: 'Links, contact info, and off-platform transaction attempts are not allowed.'
    };
  }
  
  return { isValid: true, error: null };
}

/**
 * Get a user-friendly error message for profanity
 * @deprecated Use validateMessage() instead for unified validation
 */
export function getProfanityError() {
  return 'Please keep messages professional. Profanity is not allowed.';
}

/**
 * Clean text by replacing profanity with asterisks (if we want to allow but censor)
 * Currently not used - we block instead
 */
export function censorProfanity(text) {
  if (!text || typeof text !== 'string') return text;
  
  let cleaned = text;
  const normalized = text.toLowerCase();
  
  for (const word of PROFANITY_LIST) {
    const pattern = new RegExp(`\\b${word}\\b`, 'gi');
    cleaned = cleaned.replace(pattern, '*'.repeat(word.length));
  }
  
  return cleaned;
}

